/**
 * The chat hosted inside the shell panel — the SAME `ChatSurface` the `/chat`
 * route renders (app-shell ADR-013). It is not a second chat
 * component; it is the one chat component bound by a remembered session id
 * instead of a route param (app-shell ADR-020).
 *
 * During product onboarding, when status
 * has a session_id and the phase is not complete|deferred, it binds THAT
 * session instead of the remembered one.
 */

import { useCallback, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  isProductOnboardingActive,
  useProductOnboardingStatus,
} from '../../hooks/useProductOnboarding';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { shogunComposerDraftAtom, panelChatSessionIdsAtom } from '../../stores/shogun/atoms';
import { useShogunRoutePlacement } from '../shogun/useShogunDock';
import { useShogunViewContext } from '../shogun/useShogunViewContext';
import { ViewContextIndicator } from '../shogun/ViewContextIndicator';
import { ProductOnboardingBanner } from '../onboarding/ProductOnboardingBanner';
import { ChatSurface } from './ChatSurface';

const DEFAULT_PANEL_PERSONA_NAME = 'Shogun';

export function PanelChat() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const {
    data: productStatus,
    isLoading: productStatusLoading,
    isFetched: productStatusFetched,
    isFetching: productStatusFetching,
    error: productStatusError,
    isEnabled: productStatusEnabled,
  } = useProductOnboardingStatus();

  // What the user is currently looking at, attached to each turn so the agent
  // can resolve "this" against the actual records on screen (app-shell ADR-014).
  const viewContext = useShogunViewContext();
  // A view may name its own default agent (search names Search); absent that,
  // the panel's assistant is Shogun. Either way the selector stays live.
  const routePlacement = useShogunRoutePlacement();

  const [panelSessions, setPanelSessions] = useAtom(panelChatSessionIdsAtom);
  const personaName = routePlacement?.persona ?? DEFAULT_PANEL_PERSONA_NAME;
  const panelSessionId = panelSessions[personaName] ?? null;
  const setPanelSessionId = useCallback(
    (id: string | null) => {
      setPanelSessions((prev) => {
        if (id === null) {
          if (!(personaName in prev)) return prev;
          const next = { ...prev };
          delete next[personaName];
          return next;
        }
        if (prev[personaName] === id) return prev;
        return { ...prev, [personaName]: id };
      });
    },
    [personaName, setPanelSessions],
  );
  const [composerDraft, setComposerDraft] = useAtom(shogunComposerDraftAtom);
  const [generation, setGeneration] = useState(0);
  const [chatState, setChatState] = useState('loading');

  // Product onboarding owns the DEFAULT assistant thread, not every docked
  // chat. A route that names its own agent (search names Search) keeps it —
  // otherwise the onboarding conversation silently hijacks that view and the
  // agent the user is looking at never runs.
  const routeNamesItsOwnAgent = routePlacement?.persona !== undefined;
  const productActive =
    isProductOnboardingActive(productStatus?.phase) && !routeNamesItsOwnAgent;
  // After commercial complete, product status may briefly be null while the
  // workflow becomes queryable — hold off binding a parallel session.
  const awaitingProductStatus =
    !routeNamesItsOwnAgent &&
    productStatusEnabled &&
    !productStatusError &&
    (productStatusLoading || productStatus === null);
  const onboardingSessionId =
    productActive && productStatus?.session_id ? productStatus.session_id : null;

  const onboardingSettling =
    productActive &&
    !onboardingSessionId &&
    (productStatus?.phase === 'starting' || productStatusLoading || productStatusFetching);
  // awaiting_intake (or later) without a session_id is broken — fail loud.
  const onboardingBroken =
    productActive && !onboardingSessionId && !onboardingSettling
      ? `Product onboarding phase is ${productStatus?.phase ?? 'unknown'} but session_id is missing`
      : null;

  const ready =
    isAuthenticated &&
    sdkConfigured &&
    !awaitingProductStatus &&
    (routeNamesItsOwnAgent ||
      !productStatusEnabled ||
      productStatusFetched ||
      Boolean(productStatusError)) &&
    !onboardingSettling &&
    onboardingBroken === null;

  const handleNewConversation = useCallback(() => {
    setPanelSessionId(null);
    setGeneration((n) => n + 1);
  }, [setPanelSessionId]);

  const handleSessionMissing = useCallback(() => {
    setPanelSessionId(null);
  }, [setPanelSessionId]);

  const consumeComposerDraft = useCallback(() => setComposerDraft(null), [setComposerDraft]);

  if (onboardingBroken || (productStatusError && !routeNamesItsOwnAgent)) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-body text-destructive">
        Failed to start the assistant: {onboardingBroken ?? productStatusError?.message}
      </div>
    );
  }

  if (onboardingSettling || awaitingProductStatus) {
    return (
      <div
        className="flex h-full items-center justify-center p-4 text-center text-body text-muted-foreground"
        data-testid="shogun-chat-waiting-onboarding"
      >
        Starting onboarding conversation…
      </div>
    );
  }

  if (!ready) return null;

  const boundSessionId = onboardingSessionId ?? panelSessionId ?? undefined;

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="shogun-chat"
      data-chat-state={chatState}
      data-product-onboarding={productActive ? 'active' : 'idle'}
      data-session-id={boundSessionId}
    >
      {productActive ? <ProductOnboardingBanner /> : null}
      <div className="min-h-0 flex-1">
        <ChatSurface
          // Remount on a new conversation so the composer and scroll region
          // start clean rather than inheriting the ended thread's.
          key={generation}
          sessionId={boundSessionId}
          onOpenSession={setPanelSessionId}
          onSessionCreated={setPanelSessionId}
          onSessionMissing={handleSessionMissing}
          testIdPrefix="shogun-"
          viewContext={viewContext}
          defaultPersonaName={personaName}
          composerDraft={composerDraft}
          onComposerDraftConsumed={consumeComposerDraft}
          onChatStateChange={setChatState}
          headerSlot={
            <>
              {/* "acting on: {selected}" — tells the user what "this" resolves
                  to before they confirm an action. */}
              <ViewContextIndicator selected={viewContext.selected} />
              {productActive ? null : (
                <button
                  type="button"
                  data-testid="shogun-new-conversation"
                  onClick={handleNewConversation}
                  title="Start a new conversation"
                  className="shrink-0 rounded-md bg-secondary px-2 py-0.5 text-caption text-secondary-foreground transition-colors hover:bg-secondary/80"
                >
                  New conversation
                </button>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}
